import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import {
  normalizeEnabledOrderTypes,
  enabledIdsFromConfig,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'

export type { OrderTypeConfig } from '@/lib/orderTypeSettings'

/** Resolves the per-type delivery default (aligned with backend orderTypeDeliveryFee). */
export function getOrderTypePricing(
  type: string,
  raw: OrderTypeConfig[]
): { deliveryFee: number } {
  const tid = String(type ?? '').trim().toLowerCase()
  const row = raw.find((r) => String(r.id ?? '').trim().toLowerCase() === tid)
  const deliveryFee =
    tid === 'delivery' && row && typeof row.delivery_fee === 'number' && !Number.isNaN(row.delivery_fee)
      ? Math.max(0, row.delivery_fee)
      : 0
  return { deliveryFee }
}

export interface UseEnabledOrderTypesResult {
  /** All enabled order type ids (built-in + custom) */
  enabledIds: ReadonlySet<string>
  isLoading: boolean
  /** Normalized merge of defaults + persisted rows */
  raw: OrderTypeConfig[]
}

/**
 * Which order types are enabled in Admin Settings (counter + server toggles).
 */
export function useEnabledOrderTypes(): UseEnabledOrderTypesResult {
  const { data, isLoading } = useQuery<OrderTypeConfig[]>({
    queryKey: ['settings', 'enabled_order_types'],
    queryFn: async () => {
      try {
        const res = await apiClient.getSetting('enabled_order_types')
        if (res.success && Array.isArray(res.data)) {
          return res.data as OrderTypeConfig[]
        }
      } catch {
        // Swallow network / parse errors; the fallback below keeps the POS usable.
      }
      return []
    },
  })

  const raw = useMemo(() => normalizeEnabledOrderTypes(data ?? []), [data])

  const enabledIds = useMemo<ReadonlySet<string>>(() => {
    if (!data || data.length === 0) {
      return enabledIdsFromConfig([])
    }
    return enabledIdsFromConfig(raw)
  }, [data, raw])

  return {
    enabledIds,
    isLoading,
    raw,
  }
}
